import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { 
  LayoutDashboard, 
  GraduationCap, 
  Users, 
  UserCheck,
  UserCog,
  LogOut, 
  Menu,
  X,
  Monitor,
  BookOpen,
  UserPlus,
  Award,
  HelpCircle,
  User,
  Settings,
  Mail,
  Building2,
  ClipboardList
} from 'lucide-react';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isAdmin = user?.role === 'Administrador' || user?.role === 'Formador';
  const isStudent = user?.role === 'Alumno';
  const [pendingPre, setPendingPre] = useState(0);

  useEffect(() => {
    if (!isAdmin) return;
    let active = true;
    const fetchCount = () => {
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/preinscriptions/count`)
        .then((r) => { if (active) setPendingPre(r.data?.pending || 0); })
        .catch(() => {});
    };
    fetchCount();
    const t = setInterval(fetchCount, 60000);
    return () => { active = false; clearInterval(t); };
  }, [isAdmin, location.pathname]);

  // Navegación base para todos los usuarios
  const baseNavigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  ];

  // Navegación para administradores/formadores
  const adminNavigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Cursos Presenciales', href: '/courses', icon: GraduationCap },
    { name: 'Formación Online', href: '/online-courses', icon: Monitor },
    { name: 'Alumnos', href: '/students', icon: Users },
    { name: 'Preinscripciones', href: '/preinscriptions', icon: ClipboardList },
    { name: 'Instructores', href: '/instructors', icon: UserCheck },
    { name: 'Directores', href: '/directors', icon: UserCog },
    { name: 'Gestión Usuarios', href: '/user-management', icon: Settings },
    { name: 'Usuarios Pendientes', href: '/pending-users', icon: UserPlus },
    { name: 'Plantillas Email', href: '/email-templates', icon: Mail },
  ];

  // Solo administradores ven la Intranet de gestión
  if (user?.role === 'Administrador') {
    adminNavigation.push({ name: 'Intranet Gestión', href: '/billing', icon: Building2 });
  }

  // Navegación para alumnos
  const studentNavigation = [
    { name: 'Mi Panel', href: '/student-dashboard', icon: LayoutDashboard },
    { name: 'Mis Cursos', href: '/my-courses', icon: BookOpen },
    { name: 'Catálogo Online', href: '/online-courses', icon: Monitor },
    { name: 'Evaluaciones', href: '/my-quiz-history', icon: HelpCircle },
    { name: 'Certificados', href: '/my-certificates', icon: Award },
    { name: 'Mi Perfil', href: '/user-profile', icon: User },
  ];

  const navigation = isStudent ? studentNavigation : (isAdmin ? adminNavigation : baseNavigation);

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#0F172A] transform transition-transform duration-300 ease-in-out ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0 lg:static`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-white font-manrope font-bold text-xl" data-testid="sidebar-logo">IRTA</h1>
                <p className="text-slate-400 text-xs mt-1">Healthcare Training</p>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden text-slate-400 hover:text-white"
                data-testid="sidebar-close-button"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href || 
                (item.href !== '/dashboard' && location.pathname.startsWith(item.href));
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  data-testid={`sidebar-nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`flex items-center px-4 py-3 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-[#004E89] text-white'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  <span className="flex-1">{item.name}</span>
                  {item.name === 'Preinscripciones' && pendingPre > 0 && (
                    <span className="ml-2 min-w-5 h-5 px-1.5 flex items-center justify-center rounded-full bg-amber-500 text-white text-xs font-semibold" data-testid="sidebar-preinscriptions-badge">
                      {pendingPre}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* User Info */}
          <div className="p-4 border-t border-slate-700">
            <div className="mb-4">
              <p className="text-white font-medium text-sm" data-testid="sidebar-user-name">{user?.nombre} {user?.apellidos}</p>
              <p className="text-slate-400 text-xs mt-1" data-testid="sidebar-user-role">{user?.role}</p>
            </div>
            <Link
              to="/profile"
              data-testid="sidebar-profile-link"
              className="w-full flex items-center px-4 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-800 rounded-md transition-colors mb-2"
              onClick={() => setSidebarOpen(false)}
            >
              Mi Perfil
            </Link>
            <button
              onClick={handleLogout}
              data-testid="sidebar-logout-button"
              className="w-full flex items-center px-4 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-800 rounded-md transition-colors"
            >
              <LogOut className="w-4 h-4 mr-3" />
              Cerrar sesión
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Mobile Header */}
        <div className="lg:hidden bg-white border-b border-slate-200 p-4">
          <button
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-button"
            className="text-slate-600 hover:text-slate-900"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <main className="flex-1 p-6 md:p-8 lg:p-12 overflow-y-auto">
          {children}
        </main>
      </div>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}
    </div>
  );
};

export default Layout;